import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Alert from '../global/Alert';
import Button from '../global/Button';
import PhoneInput from '../global/PhoneInput';
import BoxedArrowLink from '../global/BoxedArrowLink';
import Spinner from '../global/Spinner';
import { formatPhone, parsePhone } from '../../util/phone';
import getLogger from '../../util/logger';
import { AUTH_TYPE } from '../../constants/auth';
import { ACCOUNT_REFERRAL_TYPE } from '../../constants/account-referrals';
import PinField from '../global/PinField';
import useGoogleRecaptcha from '../../services/recaptcha/useGoogleRecaptcha';
import usePhoneAuth from './hooks/usePhoneAuth';
import CustomControl from '../global/CustomControl';

const log = getLogger('PhoneAuth');
const INACTIVITY_TIMEOUT = 20000; // 20s

const propTypes = {
  className: PropTypes.string,
  introText: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.string,
  ]),
  verificationText: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.string,
  ]),
  onLogin: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  submitText: PropTypes.string,
  submitClassName: PropTypes.string,
  pinSubmitText: PropTypes.string,
  defaultPhone: PropTypes.string,
  onEmailAuth: PropTypes.func,
  accountReferralCreate: PropTypes.func,
  enableEmailFallback: PropTypes.bool,
  onSentPin: PropTypes.func,
};

const PhoneAuth = ({
  className,
  introText = 'Verify your mobile number to enter. Waldo will text a verification code to the number below.',
  verificationText = 'Please enter the code that was sent to',
  onLogin = () => {},
  children,
  submitText = 'Send PIN Code to Verify',
  submitClassName,
  pinSubmitText = 'Submit',
  defaultPhone,
  onEmailAuth,
  accountReferralCreate,
  enableEmailFallback = true,
  onSentPin,
}) => {
  const [phone, setPhone] = useState(defaultPhone ?? null);
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneSubmitted, setPhoneSubmitted] = useState(false);
  const [showChildrenOnPinEntry, setShowChildrenOnPinEntry] = useState(false);
  const [isNotRobot, setIsNotRobot] = useState(false);
  const [isUSPhoneNumber, setIsUSPhoneNumber] = useState(true);
  const { onTokenRequest } = useGoogleRecaptcha();
  const {
    error,
    loading,
    requireHumanInput,
    blockLogin,
    pinSent,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();

  const onPhoneInputChange = newPhone => {
    setPhone(newPhone);
  };

  const onVerificationCodeChange = value => {
    setVerificationCode(value);
  };

  const onPhoneAuthRequestSuccess = () => {
    setPhoneSubmitted(true);
    setTimeout(() => {
      // Show email link after 20s if user still here.
      setShowChildrenOnPinEntry(true);
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    if (pinSent && !phoneSubmitted) {
      onSentPin?.();
      // reset robot
      setIsNotRobot(false);
      onPhoneAuthRequestSuccess();
    }
  }, [pinSent]);

  const onSubmitPhoneAuthRequest = async event => {
    event?.preventDefault();
    event?.stopPropagation();

    try {
      const captchaToken = await onTokenRequest({ action: 'login' });
      await onPhoneAuthSubmit(phone, true, captchaToken, isNotRobot);
    } catch (e) {
      log.error('onSubmitPhoneAuthRequest() :: Error:', e);
    }
  };

  const onSubmitPhoneVerify = async event => {
    event.preventDefault();
    event.stopPropagation();

    await onPhoneAuthVerify(parsePhone(phone), verificationCode, async () => {
      await accountReferralCreate(ACCOUNT_REFERRAL_TYPE.ACCOUNT_SIGNUP);
      onLogin(AUTH_TYPE.PHONE);
    });
  };

  const onResendVerificationCode = async () => {
    try {
      const captchaToken = await onTokenRequest({ action: 'login' });
      await onResendPhoneAuthCode(phone, true, captchaToken, isNotRobot);
    } catch (e) {
      log.error('onResendVerificationCode() :: Error:', e);
    }
  };

  const countryDialCodeChange = countryDialCode =>
    setIsUSPhoneNumber(countryDialCode === '+1');

  const submitClasses = classNames({
    'text-extra-bold': true,
    [submitClassName]: !!submitClassName,
  });

  const pinSubmitClasses = classNames({
    'd-block mt-0 mx-auto': true,
    [submitClassName]: !!submitClassName,
  });

  return (
    <div className={className}>
      {error && <Alert type="danger">{error}</Alert>}

      {!phoneSubmitted ? (
        <form onSubmit={onSubmitPhoneAuthRequest} method="POST">
          <p>{introText}</p>

          <PhoneInput
            id="phone"
            label="Mobile Number"
            value={phone || '+1'}
            onChange={onPhoneInputChange}
            autoFocus
            className="mb-1"
            onPhoneCodeChange={countryDialCodeChange}
          />
          {requireHumanInput && (
            <CustomControl
              type="checkbox"
              label="I'm not a robot"
              checked={isNotRobot}
              onChange={() => setIsNotRobot(prev => !prev)}
            />
          )}

          {!isUSPhoneNumber && onEmailAuth && enableEmailFallback && (
            <Alert type="info">
              Can&apos;t receive a US SMS message?{' '}
              <Button
                btnStyle="link"
                btnSize="sm"
                className="non-us-signup-link"
                onClick={onEmailAuth}
              >
                Authenticate with email
              </Button>
            </Alert>
          )}

          <div className="text-center mt-2">
            <Button
              type="submit"
              disabled={
                loading || blockLogin || (requireHumanInput && !isNotRobot)
              }
              className={submitClasses}
            >
              {loading ? <Spinner size="xs" light /> : submitText}
            </Button>
          </div>

          {children}
        </form>
      ) : (
        <form
          onSubmit={onSubmitPhoneVerify}
          method="POST"
          className="phone-auth-pin-submit-form"
        >
          {typeof verificationText === 'function' ? (
            verificationText({ phone })
          ) : (
            <p>
              {verificationText} {formatPhone(phone)}
            </p>
          )}
          <PinField
            id="phone-code"
            onChange={onVerificationCodeChange}
            onComplete={onVerificationCodeChange}
            className="mb-2"
          />
          <Button
            type="submit"
            className={pinSubmitClasses}
            disabled={loading || verificationCode.length < 4}
          >
            {loading ? <Spinner size="xs" light /> : pinSubmitText}
          </Button>
          {requireHumanInput && (
            <div className="w-100 mt-2">
              <CustomControl
                type="checkbox"
                label="I'm not a robot"
                checked={isNotRobot}
                onChange={() => setIsNotRobot(prev => !prev)}
              />
            </div>
          )}
          <BoxedArrowLink
            disabled={blockLogin || (requireHumanInput && !isNotRobot)}
            onClick={onResendVerificationCode}
            className="mt-4"
          >
            Re-send code
          </BoxedArrowLink>
          {showChildrenOnPinEntry && (
            <Alert type="info" className="mt-2">
              Not receiving the SMS?{' '}
              <Button
                btnSize="sm"
                btnStyle="link"
                className="non-us-signup-link"
                onClick={onEmailAuth}
              >
                Authenticate with email
              </Button>
            </Alert>
          )}
          {showChildrenOnPinEntry && <Fragment>{children}</Fragment>}
        </form>
      )}
    </div>
  );
};

PhoneAuth.propTypes = propTypes;

export default PhoneAuth;
